import React, { useState, useRef, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';

interface VerifyOtpPageProps {
  showToast: (message: string, type?: 'success' | 'error') => void;
}

export const VerifyOtpPage: React.FC<VerifyOtpPageProps> = ({ showToast }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [email, setEmail] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const type = searchParams.get('type') || 'recovery'; // 'recovery', 'magiclink', 'signup'

  useEffect(() => {
    // Focus sur le premier input au chargement
    inputRefs.current[0]?.focus();
  }, []);

  const handleCodeChange = (index: number, value: string) => {
    // Accepter seulement les chiffres
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus sur le prochain input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    // Backspace : revenir au input précédent si vide
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    
    // Flèche gauche/droite pour naviguer
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').trim();
    
    // Accepter seulement 6 chiffres
    if (/^\d{6}$/.test(pastedData)) {
      const newCode = pastedData.split('');
      setCode(newCode);
      inputRefs.current[5]?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      showToast('Veuillez entrer votre adresse email', 'error');
      return;
    }

    const otpCode = code.join('');
    if (otpCode.length !== 6) {
      showToast('Veuillez entrer le code à 6 chiffres', 'error');
      return;
    }

    setIsSubmitting(true);

    try {
      
      // Déterminer le type de vérification
      let verifyType: 'email' | 'recovery' | 'signup' = 'email';
      if (type === 'recovery') {
        verifyType = 'recovery';
      } else if (type === 'signup') {
        verifyType = 'signup';
      }

      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otpCode,
        type: verifyType,
      });

      if (error) {
        
        let errorMessage = 'Code invalide ou expiré';
        if (error.message.includes('expired')) {
          errorMessage = 'Le code a expiré. Demandez-en un nouveau.';
        } else if (error.message.includes('invalid')) {
          errorMessage = 'Code invalide. Vérifiez le code dans votre email.';
        }
        
        showToast(errorMessage, 'error');
        return;
      }

      showToast('Connexion réussie !', 'success');

      // Rediriger selon le type
      if (type === 'recovery') {
        navigate('/reset-password');
      } else if (type === 'signup') {
        // Marquer qu'un profil tuteur doit être créé
        localStorage.setItem('pendingTutorProfile', 'true');
        navigate('/create-profile');
      } else {
        navigate('/profile');
      }
    } catch (err) {
      showToast('Une erreur inattendue s\'est produite', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'recovery':
        return 'Réinitialiser votre mot de passe';
      case 'signup':
        return 'Confirmer votre inscription';
      default:
        return 'Se connecter';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'recovery':
        return 'Entrez le code à 6 chiffres reçu par email pour réinitialiser votre mot de passe';
      case 'signup':
        return 'Entrez le code à 6 chiffres reçu par email pour confirmer votre inscription';
      default:
        return 'Entrez le code à 6 chiffres reçu par email pour vous connecter';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-[#002c46] rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-shield-alt text-white text-2xl"></i>
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {getTitle()}
          </h2>
          <p className="text-gray-600">
            {getDescription()}
          </p>
        </div>

        {/* Formulaire */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                <i className="fas fa-envelope text-[#002c46] mr-2"></i>
                Adresse email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-transparent transition-colors"
                placeholder="votre.email@exemple.com"
                disabled={isSubmitting}
              />
            </div>

            {/* Code à 6 chiffres */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3 text-center">
                <i className="fas fa-key text-[#002c46] mr-2"></i>
                Code de vérification
              </label>
              <div className="flex justify-center gap-2 mb-2">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => {
                      inputRefs.current[index] = el;
                    }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className="w-12 h-14 text-center text-2xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-[#003d5c] focus:border-[#003d5c] transition-colors"
                    disabled={isSubmitting}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 text-center">
                Vous pouvez coller le code complet dans le premier champ
              </p>
            </div>

            {/* Information */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <i className="fas fa-info-circle text-blue-600 mr-2"></i>
                Si vous n'avez pas reçu le code, vérifiez votre dossier spam ou demandez un nouveau lien.
              </p>
            </div>

            {/* Bouton de soumission */}
            <button
              type="submit"
              disabled={isSubmitting || code.some(d => !d)}
              className="w-full py-3 px-4 bg-[#002c46] text-white font-semibold rounded-lg hover:bg-[#001a2a] focus:ring-2 focus:ring-[#003d5c] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Vérification en cours...
                </>
              ) : (
                <>
                  <i className="fas fa-check mr-2"></i>
                  Vérifier le code
                </>
              )}
            </button>
          </form>

          {/* Liens alternatifs */}
          <div className="mt-6 space-y-3 text-center">
            <Link
              to="/forgot-password"
              className="block text-gray-500 hover:text-gray-700 font-medium text-sm"
            >
              <i className="fas fa-redo mr-2"></i>
              Demander un nouveau code
            </Link>
            
            <Link
              to="/login"
              className="block text-gray-500 hover:text-gray-700 font-medium text-sm"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Retour à la connexion
            </Link>
          </div>
        </div>

        {/* Support */}
        <div className="text-center">
          <p className="text-sm text-gray-600 mb-2">
            Besoin d'aide ?
          </p>
          <Link
            to="/contact"
            className="text-[#002c46] hover:text-[#001a2a] font-medium text-sm"
          >
            <i className="fas fa-envelope mr-1"></i>
            Contactez notre support
          </Link>
        </div>
      </div>
    </div>
  );
};

